import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import elizImg from "../../assets/eliz.avif";
import e1 from "../../assets/e1.jpg";
import e2 from "../../assets/e2.jpg";

export default function StElizabeth() {
  const heroImageRef = useRef(null);
  const rafRef = useRef(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const galleryImages = [e1, e2, elizImg, e1, e2, elizImg];

  // Fade-in on scroll using IntersectionObserver
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".js-fade"));
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Parallax (optimized transform only)
  useEffect(() => {
    const imgEl = heroImageRef.current;
    if (!imgEl) return;

    const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      imgEl.style.transform = "translateY(0)";
      return;
    }

    const factor = 0.05;
    const maxOffset = 48;

    const update = () => {
      rafRef.current = null;
      const rect = imgEl.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const distFromCenter = centerY - viewportCenter;
      let translateY = Math.round(distFromCenter * factor);
      if (translateY > maxOffset) translateY = maxOffset;
      if (translateY < -maxOffset) translateY = -maxOffset;
      imgEl.style.transform = `translateY(${translateY}px)`;
    };

    const onScroll = () => {
      if (!rafRef.current) {
        rafRef.current = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  // Modal keyboard handlers
  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") setModalIndex((i) => (i + 1) % galleryImages.length);
      if (e.key === "ArrowLeft") setModalIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen, galleryImages.length]);

  const openModalAt = (idx) => {
    setModalIndex(idx);
    setModalOpen(true);
    // Keep scrolling enabled - don't lock body overflow
  };
  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <main
      className="se-page white-bg"
      aria-labelledby="se-title"
      style={{ minHeight: "100vh", paddingBottom: 64 }}
    >
      <style>{`
        :root{
          --primary: #eab308;
          --bg-white: #ffffff;
          --muted: #6b7280;
          --text: #0f172a;
          --card-bg: rgba(255,255,255,0.98);
          --glass: rgba(255,255,255,0.98);
          --radius: 14px;
          --container: 1200px;
        }

        /* Page background: plain white */
        .se-page.white-bg {
          background: var(--bg-white);
          color: var(--text);
          -webkit-font-smoothing: antialiased;
          padding-top: 48px;
          padding-left: 18px;
          padding-right: 18px;
        }

        .se-container {
          max-width: var(--container);
          margin: 0 auto;
          padding: 18px;
        }

        /* HERO layout */
        .se-hero {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 28px;
          align-items: center;
          margin-bottom: 22px;
        }

        .se-ribbon { display:inline-flex; gap:10px; color:var(--primary); font-weight:700; margin-bottom:12px; align-items:center;}
        .se-ribbon .dot { width:10px; height:10px; border-radius:50%; background:var(--primary); box-shadow: 0 6px 14px rgba(234,179,8,0.14); }

        .se-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(38px, 6vw, 72px);
          line-height: 1.2;
          margin: 0;
          color: var(--text);
        }

        .se-underline {
          width: 160px;
          height: 6px;
          background: linear-gradient(90deg,var(--primary), rgba(234,179,8,0.7));
          border-radius: 6px;
          margin-top: 12px;
          margin-bottom: 14px;
          box-shadow: 0 8px 22px rgba(234,179,8,0.08);
        }

        .se-subhead { color:var(--text); font-size:16.5px; line-height:1.6; margin-bottom:8px; }

        /* Image card */
        .se-image-card {
          width: 100%;
          max-width: 420px;
          border-radius: 12px;
          overflow: hidden;
          background: var(--glass);
          padding: 6px;
          box-shadow: 0 18px 44px rgba(15,23,42,0.10);
          border: 1px solid rgba(234,179,8,0.06);
          transition: transform 260ms ease, box-shadow 260ms ease;
        }
        .se-image-card:hover { transform: translateY(-6px); box-shadow: 0 26px 64px rgba(15,23,42,0.12); }

        .se-image {
          width:100%;
          height: 320px;
          object-fit: cover;
          display:block;
          border-radius: 10px;
          will-change: transform;
        }

        .se-image-caption { display:block; padding:8px 10px; font-size:13px; color:var(--muted); }

        /* Content */
        .se-content { margin-top: 20px; }
        .se-glass {
          background: var(--card-bg);
          border-radius: 12px;
          padding: 34px;
          box-shadow: 0 26px 90px rgba(15,23,42,0.12);
          border: 1px solid rgba(234,179,8,0.06);
          color: var(--text);
        }

        /* Section headings */
        .se-section-hero { font-size: 28px; color: var(--text); margin: 0 0 10px 0; font-weight:700; }
        .se-section-title { font-size: 20px; color: var(--text); margin: 0 0 8px 0; }

        .se-paragraph { color:#374151; line-height:1.7; margin-bottom:12px; font-size:16.5px; }

        /* Vocation & Expansion gradients */
        .se-callouts { display:grid; grid-template-columns: 1fr 1fr; gap:14px; margin-top:12px; }
        .se-callout {
          border-radius: 10px;
          padding: 18px;
          color: #071025;
          box-shadow: 0 14px 36px rgba(2,6,23,0.06);
          border: 1px solid rgba(0,0,0,0.03);
        }
        .se-callout--vocation {
          background: linear-gradient(135deg, rgba(234,179,8,0.18), rgba(255,255,255,0.98));
          border-left: 6px solid rgba(234,179,8,0.95);
        }
        .se-callout--expansion {
          background: linear-gradient(135deg, rgba(99,102,241,0.06), rgba(255,255,255,0.98));
          border-left: 6px solid rgba(99,102,241,0.12);
        }
        .se-callout h4 { margin: 0 0 8px 0; font-size: 16px; font-weight:700; }
        .se-callout p { margin: 0; color: #425166; font-size:15px; font-weight:400; line-height:1.6; }

        /* Gallery - matches Villa Theresa design */
        .se-gallery { 
          margin-top: 18px; 
          display: grid; 
          grid-template-columns: repeat(3, 1fr); 
          gap: 16px; 
        }
        .se-thumb {
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          background: #fff;
          box-shadow: 0 8px 24px rgba(15,23,42,0.1);
          border: 1px solid rgba(234,179,8,0.1);
          transition: transform 260ms ease, box-shadow 260ms ease;
          display:flex;
        }
        .se-thumb:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(15,23,42,0.15); }
        .se-thumb-inner { position: relative; width:100%; height: 140px; overflow: hidden; }
        .se-thumb img { width:100%; height:100%; object-fit: cover; display:block; transition: transform 420ms cubic-bezier(.2,.9,.38,1); }
        .se-thumb:hover img { transform: scale(1.08); }

        /* Modal lightbox - matches Villa Theresa */
        .se-modal { 
          position: fixed; 
          inset: 0; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          background: rgba(15, 23, 42, 0.75);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          z-index: 9999; 
          padding: 20px;
          overflow-y: auto;
        }
        .se-modal-inner { 
          position: relative;
          width: 100%; 
          max-width: 1600px; 
          border-radius: 16px; 
          overflow: visible; 
          background: transparent;
          margin: auto;
          max-height: 95vh;
          display: flex;
          flex-direction: column;
        }
        .se-modal-close { 
          position: absolute; 
          right: -50px; 
          top: 0; 
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px; 
          padding: 8px 12px; 
          cursor: pointer; 
          font-weight: 700;
          color: #fff;
          font-size: 18px;
          transition: all 0.2s ease;
          z-index: 10;
        }
        .se-modal-close:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }
        .se-modal img { 
          width: 100%; 
          height: auto; 
          max-height: 80vh;
          display: block; 
          object-fit: contain; 
          border-radius: 8px;
        }
        .se-modal-footer { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          gap: 12px; 
          padding: 16px 20px; 
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 0 0 16px 16px;
          color: #fff; 
          font-size: 14px;
          font-weight: 500;
        }

        /* Fade-in utility */
        .js-fade { opacity: 0; transform: translateY(42px); transition: opacity 420ms cubic-bezier(.16,.9,.3,1), transform 420ms cubic-bezier(.16,.9,.3,1); will-change: opacity, transform; }
        .js-fade.visible { opacity:1; transform: translateY(0); }

        .map-embed {
          width: 100%;
          max-width: 900px;
          margin: 20px auto;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 26px 64px rgba(15, 23, 42, 0.12);
        }

        /* Mobile Optimizations - matches Villa Theresa */
        @media (max-width: 980px) {
          .se-hero { grid-template-columns: 1fr; }
          .se-image { height: 260px; }
          .se-callouts { grid-template-columns: 1fr; }
          .se-gallery { grid-template-columns: repeat(2, 1fr); }
          
          .se-modal-close {
            right: 10px;
            top: 10px;
          }
        }

        @media (max-width: 768px) {
          .se-page.white-bg {
            padding-left: 4px;
            padding-right: 4px;
          }
          
          .se-container {
            padding: 4px;
          }
          
          .se-glass {
            padding: 8px;
          }
          
          .se-callout {
            padding: 8px;
          }
          
          .se-title {
            text-align: center;
          }
          
          .se-underline {
            margin-left: auto;
            margin-right: auto;
          }
          
          .se-subhead {
            text-align: center;
          }
          
          .se-section-hero,
          .se-section-title {
            text-align: center;
          }
          
          .se-paragraph {
            text-align: center;
          }
          
          .se-callout h4 {
            text-align: center;
          }
          
          .se-callout p {
            text-align: center;
          }
          
          .se-image-card {
            margin: 0 auto;
          }
          
          .se-gallery { 
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          
          .se-thumb-inner {
            height: 120px;
          }
        }

        @media (max-width: 480px) {
          .se-page.white-bg {
            padding-left: 2px;
            padding-right: 2px;
          }
          
          .se-container {
            padding: 2px;
          }
          
          .se-glass {
            padding: 6px;
          }
          
          .se-callout {
            padding: 6px;
          }
          
          .se-gallery { 
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
          }
          
          .se-thumb-inner {
            height: 100px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .se-image-card, .se-thumb, .se-thumb img, .js-fade { transition: none !important; transform: none !important; opacity: 1 !important; }
          .se-modal-inner { animation: none !important; }
        }
      `}</style>

      <div className="se-container">
        {/* HERO */}
        <section className="se-hero">
          <div>
            <div className="se-ribbon"></div>

            <h1 id="se-title" className="se-title js-fade" style={{ transitionDelay: "40ms" }}>
              St. Elizabeth's<br />Hospital
            </h1>

            <div className="se-underline js-fade" style={{ transitionDelay: "120ms" }} />

            <p className="se-subhead js-fade" style={{ transitionDelay: "180ms" }}>
              The Foundress — <strong>Jeanne Haze</strong>
            </p>

            <p className="js-fade" style={{ color: "var(--muted)", marginTop: 8, transitionDelay: "220ms", textAlign: "center" }}>
              The Congregation of the Daughters of the Cross: care, education and service from Liège to the world.
            </p>
          </div>

          <div className="se-image-card js-fade" style={{ transitionDelay: "260ms" }}>
            <img
              ref={heroImageRef}
              className="se-image"
              src={elizImg}
              alt="St. Elizabeth's Hospital"
              loading="lazy"
              decoding="async"
            />
            <span className="se-image-caption">St. Elizabeth's Hospital — compassionate care since 1876</span>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <section className="se-content js-fade" style={{ transitionDelay: "320ms" }}>
          <article className="se-glass" role="region" aria-labelledby="se-about">
            <h2 id="se-about" className="se-section-hero">The Foundress</h2>

            <p className="se-paragraph">
              The foundress of the Congregation of the Daughters of the Cross, Jeanne Haze, was born in Liège, Belgium, in February 1782.
              She came from a loving family which gave her spiritual strength throughout her life. Her first experience of suffering came as a
              result of the French Revolution when Jeanne and her family were exiled to Germany. During that time her father died; the family lost
              everything and Jeanne became conscious of the transitory nature of material belongings.
            </p>

            {/* Vocation & Expansion */}
            <div style={{ marginTop: 8 }}>
              <h3 className="se-section-title">Vocation & Expansion</h3>

              <div className="se-callouts" style={{ marginTop: 12 }}>
                <div className="se-callout se-callout--vocation js-fade" style={{ transitionDelay: "380ms" }}>
                  <h4 id="vocation-title">Vocation</h4>
                  <p>
                    Jeanne and her sister Ferdinande were drawn early to serve those most in need. In response to Dean Cloes of St Barthélemy they
                    opened a school for poor children, and were soon joined by other young women seeking the religious life. The first constitutions
                    for the future Daughters of the Cross were prepared with the assistance of Father Habets.
                  </p>
                </div>

                <div className="se-callout se-callout--expansion js-fade" style={{ transitionDelay: "420ms" }}>
                  <h4 id="expansion-title">Expansion</h4>
                  <p>
                    On 8 September 1833 Jeanne (Mother Marie Thérèse) and her sister (Mother Aloysia) made final commitments. The community answered
                    local needs — nursing the sick, teaching children and adults, caring for prisoners — and soon founded communities in Germany (1849),
                    India (1861) and England (1863). By the time Blessed Marie Thérèse died in 1876 the congregation had grown substantially.
                  </p>
                </div>
              </div>
            </div>

            {/* St. Elizabeth's Hospital */}
            <div style={{ marginTop: 18 }}>
              <h3 className="se-section-title">St. Elizabeth's Hospital</h3>

              <p className="se-paragraph">
                The little community's original promise — "If ever I start a Congregation I will have sisters who care for the sick" — became reality as
                sisters provided nursing care in peoples' homes and later in hospital settings. Over time the congregation established St. Elizabeth's Hospital
                to provide compassionate healthcare rooted in dignity and service.
              </p>

              <p className="se-paragraph">
                The hospital's work reflects the early spirit of the congregation: practical care for the vulnerable, a commitment to education and
                outreach, and an approach that blends clinical skill with respect and pastoral compassion.
              </p>
            </div>

            {/* Legacy */}
            <div style={{ marginTop: 18 }}>
              <h3 className="se-section-title">Legacy</h3>
              <p className="se-paragraph">
                Before Blessed Marie Thérèse's death on 7 January 1876, she had welcomed more than 900 Sisters into the congregation and founded 51 communities.
                Her early intention to care for the sick lives on in hospitals, schools and outreach centres that continue to serve communities across the world.
              </p>

              <p className="se-paragraph">
                For more on the Daughters of the Cross, historical sources and archives, or to inquire about the hospital's services, contact the site administrator.
              </p>
            </div>

            {/* Gallery */}
            <div style={{ marginTop: 20 }}>
              <h3 className="se-section-title">Gallery</h3>
              <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6, textAlign: "center" }}>Tap or click any image to open.</p>

              <div className="se-gallery" role="list" aria-label="St. Elizabeth's gallery">
                {galleryImages.map((src, idx) => (
                  <div
                    key={idx}
                    className="se-thumb js-fade"
                    onClick={() => openModalAt(idx)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openModalAt(idx); }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open gallery image ${idx + 1}`}
                    style={{ transitionDelay: `${700 + idx * 40}ms` }}
                  >
                    <div className="se-thumb-inner">
                      <img src={src} alt={`St. Elizabeth's ${idx + 1}`} loading="lazy" decoding="async" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 18, color: "var(--muted)", fontSize: 14, textAlign: "center" }}>
              
            </p>

            {/* MAP SECTION */}
            <div className="js-fade" style={{ transitionDelay: "880ms", marginTop: 28 }}>
              <h3 className="se-section-title">Location</h3>
              <p className="se-paragraph">Find us on the map below:</p>
              <div className="map-embed">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3773.606527947049!2d72.79300997489666!3d18.948802682229726!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7cdfb1d1f1def%3A0xca0e77edf23494a3!2sSt.%20Elizabeths%20Hospital%2C%20Teen%20Batti%2C%20Malabar%20Hill%2C%20Mumbai%2C%20Maharashtra%20400006!5e0!3m2!1sen!2sin!4v1760622632281!5m2!1sen!2sin"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: 12, boxShadow: "0 18px 44px rgba(15,23,42,0.1)" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps location"
                ></iframe>
              </div>
            </div>
          </article>
        </section>
      </div>

      {/* Modal lightbox - matches Villa Theresa design */}
      {isModalOpen && (
        <div className="se-modal" role="dialog" aria-modal="true" aria-label="Image viewer" onClick={closeModal}>
          <div className="se-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button className="se-modal-close" aria-label="Close viewer" onClick={closeModal}>✕</button>

            <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minHeight: 0, padding: '10px', background: 'transparent' }}>
              <button
                aria-label="Previous image"
                onClick={() => setModalIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length)}
                style={{ 
                  background: "rgba(255, 255, 255, 0.1)", 
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)", 
                  fontSize: 32, 
                  padding: "12px 20px", 
                  cursor: "pointer", 
                  color: "#fff", 
                  flexShrink: 0, 
                  borderRadius: 12,
                  fontWeight: 300,
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                onMouseOut={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"}
              >
                ‹
              </button>

              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                <img
                  src={galleryImages[modalIndex]}
                  alt={`St. Elizabeth's large ${modalIndex + 1}`}
                  loading="eager"
                  style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", display: "block", borderRadius: 12, boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)" }}
                />
              </div>

              <button
                aria-label="Next image"
                onClick={() => setModalIndex((i) => (i + 1) % galleryImages.length)}
                style={{ 
                  background: "rgba(255, 255, 255, 0.1)", 
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)", 
                  fontSize: 32, 
                  padding: "12px 20px", 
                  cursor: "pointer", 
                  color: "#fff", 
                  flexShrink: 0, 
                  borderRadius: 12,
                  fontWeight: 300,
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                onMouseOut={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"}
              >
                ›
              </button>
            </div>

            <div className="se-modal-footer" style={{ justifyContent: "space-between" }}>
              <div style={{ color: "#fff", fontWeight: 600 }}>Image {modalIndex + 1} of {galleryImages.length}</div>
              <div style={{ display: "flex", gap: 8 }}>
                {galleryImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setModalIndex(i)}
                    aria-label={`Go to image ${i + 1}`}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 10,
                      border: i === modalIndex ? "2px solid #eab308" : "2px solid transparent",
                      padding: 0,
                      background: i === modalIndex ? "#eab308" : "rgba(255, 255, 255, 0.4)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: i === modalIndex ? "0 0 8px rgba(234, 179, 8, 0.6)" : "none"
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}